// FILE: sideChatAnchorStore.ts
// Purpose: Carry the transcript fragment a side chat is being opened over, from
//          the selection toolbar to the panel that renders it.
// Layer: UI state store
// Exports: the store and its per-thread hook.

import type { ThreadId } from "@taskin-code/contracts";
import { create } from "zustand";

import type { MessageQuotedContextSelection } from "./lib/quotedContext";

/**
 * Not persisted, and keyed by the *parent* thread.
 *
 * Not persisted because an anchor only matters until the first message is sent —
 * after that the quote lives in the child thread's own transcript, which is
 * durable. Restoring a stale anchor after a reload would offer to start a
 * conversation about a fragment the user has long since scrolled past.
 *
 * Keyed by parent thread rather than held as a single value because two panes of
 * a split view can each have a side chat opening over their own thread, and one
 * selection must not retitle the other's.
 */
interface SideChatAnchorStore {
  anchorByParentThreadId: Record<string, MessageQuotedContextSelection | undefined>;
  setSideChatAnchor: (
    parentThreadId: ThreadId,
    anchor: MessageQuotedContextSelection | null,
  ) => void;
}

export const useSideChatAnchorStore = create<SideChatAnchorStore>()((set) => ({
  anchorByParentThreadId: {},
  setSideChatAnchor: (parentThreadId, anchor) =>
    set((state) => {
      if (anchor === null) {
        if (!Object.hasOwn(state.anchorByParentThreadId, parentThreadId)) return state;
        const anchorByParentThreadId = { ...state.anchorByParentThreadId };
        delete anchorByParentThreadId[parentThreadId];
        return { anchorByParentThreadId };
      }
      return {
        anchorByParentThreadId: { ...state.anchorByParentThreadId, [parentThreadId]: anchor },
      };
    }),
}));

export function useSideChatAnchor(
  parentThreadId: ThreadId | null,
): MessageQuotedContextSelection | null {
  return useSideChatAnchorStore((store) =>
    parentThreadId ? (store.anchorByParentThreadId[parentThreadId] ?? null) : null,
  );
}
